import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { BatchPickService } from '../batchpick.service';
import { BatchPickState, IssueDetail } from '../models/batchpick-state';
import { VerifyFinishService } from '../verify/verify-finish.service';
import { NsTrayPrintService } from '../../Services/ns-tray-print.service';

@Component({
  selector: 'app-batchpick-issues',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './issues.component.html',
  styleUrl: './issues.component.css',
})
export class IssuesComponent implements OnDestroy {
  batchId!: string;
  issues: IssueDetail[] = [];
  loading = false;
  finishing = false;
  /** Finished on the server, with a bag-label problem shown: Finish now goes to the verify screen. */
  finished = false;
  private state?: BatchPickState;
  banner: { type: 'warn' | 'error' | 'success'; text: string } | null = null;
  private traySub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: BatchPickService,
    private finisher: VerifyFinishService,
    private tray: NsTrayPrintService,
  ) {}

  ngOnInit() {
    this.batchId = this.route.snapshot.paramMap.get('batchId')!;
    this.refresh();
    // Finishing an MPS batch prints its bag labels, so NS Tray must be connected first.
    this.api.state(this.batchId).subscribe(s => {
      this.state = s;
      if (s.source !== 'Mps' || this.traySub) return;
      this.tray.connect();
      this.traySub = this.tray.alerts$.subscribe(a =>
        (this.banner = { type: a.type === 'warning' ? 'warn' : a.type, text: a.message }));
    });
  }

  ngOnDestroy() {
    this.traySub?.unsubscribe();
  }

  refresh() {
    this.loading = true;
    this.api.issues(this.batchId).subscribe({
      next: list => { this.issues = list; this.loading = false; },
      error: () => { this.loading = false; },
    });
  }

  resolve(i: IssueDetail) {
    const note = i.kind === 'WrongBasket' ? 'moved' : 'returned-to-stock';
    this.api.resolveIssue(this.batchId, i.id, note).subscribe(() => this.refresh());
  }

  async finish(): Promise<void> {
    if (this.finished) {
      this.router.navigate(['/batchpick', this.batchId, 'verify']);
      return;
    }
    if (this.finishing) return;
    this.finishing = true;
    try {
      const labelProblem = await this.finisher.finish(this.batchId, this.state);
      if (labelProblem === null) {
        this.router.navigate(['/batchlist']);
        return;
      }
      this.finished = true;
      this.banner = { type: 'error', text: `Batch finished, but ${labelProblem}. Use Reprint bag labels on the verify screen.` };
    } catch {
      this.banner = { type: 'error', text: 'Could not finish verifying — retry.' };
    } finally {
      this.finishing = false;
    }
  }
}
